/**
 * API Service Layer - Backend Integration
 */

import {
  Empresa,
  Usuario,
  UsuarioDesempenho,
  Departamento,
  Cargo,
  ResultadoImportacaoCargos,
  ResultadoCadastroInteligente,
  ResumoConvites,
  CargoResposta,
  OKR,
  KeyResult,
  OKRAtividade,
  Notificacao,
  AuthUser,
  LoginCredentials,
  AuthResponse,
  ApiResponse,
  UserRole,
  OKRStatus,
  KeyResultApproval,
  OKRAtividadeTipo,
  UsuarioEmpresa,
  Organograma,
  TipoOrganograma,
  OrganogramaHistorico,
  OrganogramaPermissao,
  OrganogramaNode,
  FichaCadastral,
  Habilidade,
  CursoPessoal,
} from '@/types';
import { apiClient, ApiRequestError } from '@/lib/api-client';
import type { DadosGeracao } from '@/lib/organogramaGerador';
import { mensagemDoErroCargoGestor } from '@/lib/cargoErros';

type RuntimeEnvWindow = Window & {
  __ENV__?: {
    VITE_API_URL?: string;
  };
};

const getApiBaseUrl = () =>
  (typeof window !== 'undefined' && (window as RuntimeEnvWindow).__ENV__?.VITE_API_URL)
  || import.meta.env.VITE_API_URL
  || 'http://localhost:3001/api';

export type OrganogramaCanvasNodePayload = Record<string, unknown>;
export type OrganogramaCanvasEdgePayload = Record<string, unknown>;
export type OrganogramaCanvasResponse = {
  organograma: Organograma;
  nodes: OrganogramaCanvasNodePayload[];
  edges: OrganogramaCanvasEdgePayload[];
  canEdit: boolean;
};

// ============================================
// AUTH SERVICE - Backend Integration
// ============================================

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao fazer login',
      };
    }
  },
  
  async validateToken(token: string): Promise<AuthUser | null> {
    try {
      // Header sobrescrito diretamente na requisição — nunca escrever o token no
      // localStorage aqui: essa chave é compartilhada entre abas, e um swap-restore
      // temporário podia ser sobrescrito por um login/logout concorrente em outra aba,
      // deixando o token errado salvo (causa suspeita de um 401 intermitente já visto
      // em produção, ver ATUALIZACOES.md 2026-08-29).
      const response = await apiClient.get<{ success: boolean; user?: AuthUser }>('/auth/validate', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.success && response.user) {
        return response.user;
      }

      return null;
    } catch (error) {
      return null;
    }
  },
};

// ============================================
// EMPRESA SERVICE - Backend Integration
// ============================================

export const empresaService = {
  async getAll(): Promise<ApiResponse<Empresa[]>> {
    try {
      const response = await apiClient.get<ApiResponse<Empresa[]>>('/empresas');
      // Converter data_criacao de Date para string se necessário
      if (response.success && response.data) {
        response.data = response.data.map(emp => ({
          ...emp,
          data_criacao: typeof emp.data_criacao === 'string' 
            ? emp.data_criacao 
            : new Date(emp.data_criacao).toISOString().split('T')[0],
        }));
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar empresas',
        data: [],
      };
    }
  },
  
  async getById(id: string): Promise<ApiResponse<Empresa | null>> {
    try {
      const response = await apiClient.get<ApiResponse<Empresa>>(`/empresas/${id}`);
      if (response.success && response.data) {
        response.data = {
          ...response.data,
          data_criacao: typeof response.data.data_criacao === 'string' 
            ? response.data.data_criacao 
            : new Date(response.data.data_criacao).toISOString().split('T')[0],
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar empresa',
        data: null,
      };
    }
  },
  
  async create(data: Omit<Empresa, 'id'> & { modulos_habilitados?: string[] }): Promise<ApiResponse<Empresa>> {
    try {
      const response = await apiClient.post<ApiResponse<Empresa>>('/empresas', {
        nome: data.nome,
        status: data.status || 'ativa',
        modulos_habilitados: data.modulos_habilitados,
        organograma_theme_default: data.organograma_theme_default,
        cnpj: data.cnpj,
        razao_social: data.razao_social,
        nome_fantasia: data.nome_fantasia,
        cep: data.cep,
        logradouro: data.logradouro,
        numero: data.numero,
        complemento: data.complemento,
        bairro: data.bairro,
        cidade: data.cidade,
        estado: data.estado,
      });
      if (response.success && response.data) {
        response.data = {
          ...response.data,
          data_criacao: typeof response.data.data_criacao === 'string'
            ? response.data.data_criacao
            : new Date(response.data.data_criacao).toISOString().split('T')[0],
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar empresa',
        data: {} as Empresa,
      };
    }
  },
  
  async update(id: string, data: Partial<Empresa> & { modulos_habilitados?: string[] }): Promise<ApiResponse<Empresa | null>> {
    try {
      const updateData: {
        nome?: string;
        status?: 'ativa' | 'inativa';
        modulos_habilitados?: string[];
        organograma_theme_default?: Empresa['organograma_theme_default'];
      } & Partial<Pick<Empresa, 'cnpj' | 'razao_social' | 'nome_fantasia' | 'cep' | 'logradouro' | 'numero' | 'complemento' | 'bairro' | 'cidade' | 'estado'>> = {};
      if (data.nome) updateData.nome = data.nome;
      if (data.status) updateData.status = data.status;
      if (data.modulos_habilitados) updateData.modulos_habilitados = data.modulos_habilitados;
      if (data.organograma_theme_default !== undefined) updateData.organograma_theme_default = data.organograma_theme_default;

      const camposCadastrais = ['cnpj', 'razao_social', 'nome_fantasia', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'estado'] as const;
      for (const campo of camposCadastrais) {
        if (campo in data) updateData[campo] = data[campo];
      }

      const response = await apiClient.put<ApiResponse<Empresa>>(`/empresas/${id}`, updateData);
      if (response.success && response.data) {
        response.data = {
          ...response.data,
          data_criacao: typeof response.data.data_criacao === 'string'
            ? response.data.data_criacao
            : new Date(response.data.data_criacao).toISOString().split('T')[0],
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar empresa',
        data: null,
      };
    }
  },
  
  async toggleStatus(id: string): Promise<ApiResponse<Empresa | null>> {
    try {
      const response = await apiClient.patch<ApiResponse<Empresa>>(`/empresas/${id}/toggle-status`);
      if (response.success && response.data) {
        response.data = {
          ...response.data,
          data_criacao: typeof response.data.data_criacao === 'string' 
            ? response.data.data_criacao 
            : new Date(response.data.data_criacao).toISOString().split('T')[0],
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao alterar status da empresa',
        data: null,
      };
    }
  },
  
  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(`/empresas/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir empresa',
        data: false,
      };
    }
  },

  async getAvailableModules(): Promise<ApiResponse<Array<{ key: string; label: string; description: string; icon: string }>>> {
    try {
      const response = await apiClient.get<ApiResponse<Array<{ key: string; label: string; description: string; icon: string }>>>('/empresas/modules/list');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar módulos disponíveis',
        data: [],
      };
    }
  },
};

// ============================================
// USUARIO SERVICE - Backend Integration
// ============================================

// Interface para o formato de usuário retornado pelo backend
interface UsuarioBackend {
  id: string;
  nome: string;
  email: string;
  tipo: string;
  empresa_id: string | null;
  departamento_id?: string | null;
  cargo_id?: string | null;
  cargo_nome?: string | null;
  gestor_usuario_id?: string | null;
  gestor_nome?: string | null;
  usuarioEmpresas?: UsuarioEmpresa[];
  notificacoes_novos_okrs?: boolean;
  notificacoes_atualizacao_okr?: boolean;
  notificacoes_cancelamento_okr?: boolean;
  notificacoes_alteracao_key_result?: boolean;
  email_notificacao_1?: string | null;
  email_notificacao_2?: string | null;
  visualiza_apenas_proprio_dep?: boolean;
  avatar_url?: string | null;
  ativo?: boolean;
  ultimo_acesso?: string | null;
  total_acessos?: number | string;
  [key: string]: unknown;
}

interface PaginatedUsuariosResponse extends ApiResponse<UsuarioBackend[]> {
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UsuarioPaginationParams {
  page: number;
  limit?: number;
  search?: string;
  empresa_id?: string;
  departamento_id?: string;
  tipo?: string;
}

export interface UsuarioPaginatedResult extends ApiResponse<Usuario[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const mapUsuarioFromBackend = (usuario: UsuarioBackend): Usuario => {
  const emailsNotificacao: string[] = [];
  if (usuario.email_notificacao_1?.trim()) {
    emailsNotificacao.push(usuario.email_notificacao_1.trim());
  }
  if (usuario.email_notificacao_2?.trim()) {
    emailsNotificacao.push(usuario.email_notificacao_2.trim());
  }

  return {
    id: usuario.id,
    nome: usuario.nome,
    email: usuario.email,
    senha: '',
    tipo: usuario.tipo as UserRole,
    empresa_id: usuario.empresa_id,
    departamento_id: usuario.departamento_id,
    cargo_id: usuario.cargo_id ?? null,
    cargo_nome: usuario.cargo_nome ?? null,
    gestor_usuario_id: usuario.gestor_usuario_id ?? null,
    gestor_nome: usuario.gestor_nome ?? null,
    usuarioEmpresas: usuario.usuarioEmpresas ?? [],
    visualiza_apenas_proprio_dep: usuario.visualiza_apenas_proprio_dep ?? false,
    avatar_url: usuario.avatar_url ?? null,
    ativo: usuario.ativo ?? true,
    ultimo_acesso: usuario.ultimo_acesso ?? null,
    total_acessos: usuario.total_acessos !== undefined ? Number(usuario.total_acessos) : undefined,
    notificacoes: {
      novos_okrs: usuario.notificacoes_novos_okrs ?? true,
      atualizacao_okr: usuario.notificacoes_atualizacao_okr ?? true,
      cancelamento_okr: usuario.notificacoes_cancelamento_okr ?? true,
      alteracao_key_result: usuario.notificacoes_alteracao_key_result ?? true,
      emails_notificacao: emailsNotificacao,
    },
  };
};

export const usuarioService = {
  async getPaginated(params: UsuarioPaginationParams): Promise<UsuarioPaginatedResult> {
    try {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit ?? 10));
      if (params.search?.trim()) query.set('search', params.search.trim());
      if (params.empresa_id) query.set('empresa_id', params.empresa_id);
      if (params.departamento_id) query.set('departamento_id', params.departamento_id);
      if (params.tipo) query.set('tipo', params.tipo);

      const response = await apiClient.get<PaginatedUsuariosResponse>(`/usuarios?${query.toString()}`);
      const data = response.success && response.data ? response.data.map(mapUsuarioFromBackend) : [];
      return {
        ...response,
        data,
        pagination: response.pagination ?? {
          page: params.page,
          limit: params.limit ?? 10,
          total: data.length,
          totalPages: 1,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar usuários',
        data: [],
        pagination: {
          page: params.page,
          limit: params.limit ?? 10,
          total: 0,
          totalPages: 0,
        },
      };
    }
  },

  async getAll(): Promise<ApiResponse<Usuario[]>> {
    try {
      const response = await apiClient.get<ApiResponse<UsuarioBackend[]>>('/usuarios');
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapUsuarioFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar usuários',
        data: [],
      };
    }
  },
  
  async getByEmpresa(empresaId: string): Promise<ApiResponse<Usuario[]>> {
    try {
      const response = await apiClient.get<ApiResponse<UsuarioBackend[]>>(`/usuarios?empresa_id=${empresaId}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapUsuarioFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar usuários',
        data: [],
      };
    }
  },
  
  async getById(id: string): Promise<ApiResponse<Usuario | null>> {
    try {
      const response = await apiClient.get<ApiResponse<UsuarioBackend>>(`/usuarios/${id}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapUsuarioFromBackend(response.data),
        };
      }
      return { ...response, data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar usuário',
        data: null,
      };
    }
  },
  
  async create(data: Omit<Usuario, 'id'>): Promise<ApiResponse<Usuario>> {
    try {
      const createData: Record<string, unknown> = {
        nome: data.nome,
        email: data.email,
        senha: data.senha,
        tipo: data.tipo || 'comum',
        empresa_id: data.empresa_id || null,
        departamento_id: data.departamento_id || null,
        visualiza_apenas_proprio_dep: data.visualiza_apenas_proprio_dep ?? false,
      };
      // null limpa; ausente não mexe (contrato de cargos)
      if (data.cargo_id !== undefined) createData.cargo_id = data.cargo_id;
      if (data.gestor_usuario_id !== undefined) createData.gestor_usuario_id = data.gestor_usuario_id;

      if (data.notificacoes) {
        createData.notificacoes = data.notificacoes;
      }
      
      const response = await apiClient.post<ApiResponse<UsuarioBackend>>('/usuarios', createData);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapUsuarioFromBackend(response.data),
        };
      }
      return { ...response, data: {} as Usuario };
    } catch (error) {
      return {
        success: false,
        error: mensagemDoErroCargoGestor(error, 'Erro ao criar usuário'),
        data: {} as Usuario,
      };
    }
  },
  
  async update(id: string, data: Partial<Usuario>): Promise<ApiResponse<Usuario | null>> {
    try {
      const updateData: Record<string, unknown> = {};
      if (data.nome) updateData.nome = data.nome;
      if (data.email) updateData.email = data.email;
      if (data.senha) updateData.senha = data.senha;
      if (data.tipo) updateData.tipo = data.tipo;
      if (data.empresa_id !== undefined) updateData.empresa_id = data.empresa_id;
      if (data.departamento_id !== undefined) updateData.departamento_id = data.departamento_id;
      if (data.visualiza_apenas_proprio_dep !== undefined) updateData.visualiza_apenas_proprio_dep = data.visualiza_apenas_proprio_dep;
      if (data.cargo_id !== undefined) updateData.cargo_id = data.cargo_id;
      if (data.gestor_usuario_id !== undefined) updateData.gestor_usuario_id = data.gestor_usuario_id;
      if (data.notificacoes) {
        updateData.notificacoes = data.notificacoes;
      }
      
      const response = await apiClient.put<ApiResponse<UsuarioBackend> & { token?: string }>(`/usuarios/${id}`, updateData);
      // Troca da própria senha invalida o token antigo; o backend devolve um novo para manter a sessão.
      if (response.token) apiClient.setToken(response.token);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapUsuarioFromBackend(response.data),
        };
      }
      return { ...response, data: null };
    } catch (error) {
      return {
        success: false,
        error: mensagemDoErroCargoGestor(error, 'Erro ao atualizar usuário'),
        data: null,
      };
    }
  },
  
  /** `escopo: 'acesso_da_empresa'` = a conta atende outra empresa: só o acesso a esta empresa foi removido. */
  async delete(id: string): Promise<ApiResponse<boolean> & { escopo?: 'acesso_da_empresa' }> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean> & { escopo?: 'acesso_da_empresa' }>(`/usuarios/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir usuário',
        data: false,
      };
    }
  },

  async getProfile(): Promise<ApiResponse<Usuario | null>> {
    try {
      const response = await apiClient.get<ApiResponse<UsuarioBackend>>('/usuarios/perfil');
      if (response.success && response.data) {
        return {
          ...response,
          data: mapUsuarioFromBackend(response.data),
        };
      }
      return { ...response, data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar perfil',
        data: null,
      };
    }
  },

  async updatePassword(senhaAtual: string, novaSenha: string, confirmarSenha: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.put<ApiResponse<{ message: string; token?: string }>>('/usuarios/perfil/senha', {
        senhaAtual,
        novaSenha,
        confirmarSenha,
      });
      // A troca de senha invalida o token antigo; o backend devolve um novo para manter a sessão.
      if (response.data?.token) apiClient.setToken(response.data.token);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao alterar senha',
        data: { message: '' },
      };
    }
  },

  async uploadAvatar(id: string, file: File): Promise<ApiResponse<{ avatar_url: string | null }>> {
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      return await apiClient.postMultipart<ApiResponse<{ avatar_url: string | null }>>(`/usuarios/${id}/avatar`, fd);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar foto', data: { avatar_url: null } };
    }
  },

  async removeAvatar(id: string): Promise<ApiResponse<{ avatar_url: string | null }>> {
    try {
      return await apiClient.delete<ApiResponse<{ avatar_url: string | null }>>(`/usuarios/${id}/avatar`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao remover foto', data: { avatar_url: null } };
    }
  },

  async getDesempenho(options: { empresaId?: string; todas?: boolean } = {}): Promise<ApiResponse<UsuarioDesempenho[]>> {
    try {
      const query = new URLSearchParams();
      if (options.todas) {
        query.set('todas', 'true');
      } else if (options.empresaId) {
        query.set('empresa_id', options.empresaId);
      }
      const qs = query.toString();
      return await apiClient.get<ApiResponse<UsuarioDesempenho[]>>(`/usuarios/dashboard${qs ? `?${qs}` : ''}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar desempenho', data: [] };
    }
  },
};

// ============================================
// DEPARTAMENTO SERVICE - Backend Integration
// ============================================

interface PaginatedDepartamentosResponse extends ApiResponse<Departamento[]> {
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface DepartamentoPaginationParams {
  page: number;
  limit?: number;
  empresa_id?: string;
}

export interface DepartamentoPaginatedResult extends ApiResponse<Departamento[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const departamentoService = {
  async getPaginated(params: DepartamentoPaginationParams): Promise<DepartamentoPaginatedResult> {
    try {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit ?? 10));
      if (params.empresa_id) query.set('empresa_id', params.empresa_id);

      const response = await apiClient.get<PaginatedDepartamentosResponse>(`/departamentos?${query.toString()}`);
      const data = response.success && response.data ? response.data : [];
      return {
        ...response,
        data,
        pagination: response.pagination ?? {
          page: params.page,
          limit: params.limit ?? 10,
          total: data.length,
          totalPages: 1,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar departamentos',
        data: [],
        pagination: {
          page: params.page,
          limit: params.limit ?? 10,
          total: 0,
          totalPages: 0,
        },
      };
    }
  },

  async getAll(): Promise<ApiResponse<Departamento[]>> {
    try {
      const response = await apiClient.get<ApiResponse<Departamento[]>>('/departamentos');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar departamentos',
        data: [],
      };
    }
  },
  
  async getByEmpresa(empresaId: string): Promise<ApiResponse<Departamento[]>> {
    try {
      const response = await apiClient.get<ApiResponse<Departamento[]>>(`/departamentos?empresa_id=${empresaId}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar departamentos',
        data: [],
      };
    }
  },
  
  async getById(id: string): Promise<ApiResponse<Departamento | null>> {
    try {
      const response = await apiClient.get<ApiResponse<Departamento>>(`/departamentos/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar departamento',
        data: null,
      };
    }
  },
  
  async create(data: Omit<Departamento, 'id'>): Promise<ApiResponse<Departamento>> {
    try {
      const response = await apiClient.post<ApiResponse<Departamento>>('/departamentos', {
        nome: data.nome,
        empresa_id: data.empresa_id,
      });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar departamento',
        data: {} as Departamento,
      };
    }
  },
  
  async update(id: string, data: Partial<Departamento>): Promise<ApiResponse<Departamento | null>> {
    try {
      const updateData: { nome?: string; empresa_id?: string } = {};
      if (data.nome) updateData.nome = data.nome;
      if (data.empresa_id) updateData.empresa_id = data.empresa_id;
      
      const response = await apiClient.put<ApiResponse<Departamento>>(`/departamentos/${id}`, updateData);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar departamento',
        data: null,
      };
    }
  },
  
  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(`/departamentos/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir departamento',
        data: false,
      };
    }
  },
};

// ============================================
// CARGO SERVICE
// ============================================

/** Converte um erro HTTP em resposta com `code` e campos extras (cargo_id, total_pessoas) do contrato. */
function erroCargo<T>(error: unknown, padrao: string, vazio: T): CargoResposta<T> {
  if (error instanceof ApiRequestError) {
    const { cargo_id, total_pessoas } = error.payload;
    return {
      success: false,
      error: error.message,
      data: vazio,
      code: error.code,
      cargo_id: typeof cargo_id === 'string' ? cargo_id : undefined,
      total_pessoas: typeof total_pessoas === 'number' ? total_pessoas : undefined,
    };
  }
  return { success: false, error: error instanceof Error ? error.message : padrao, data: vazio };
}

/** Campos editáveis do cargo (os opcionais vazios viram `null`). */
type CargoCampos = { nome: string; quantidade_quadro?: number | null; departamento_id?: string | null };

export const cargoService = {
  /** Cargos ativos da empresa (ou, com `lixeira`, só os excluídos). Superadmin passa `empresa_id`. */
  async getAll(params: { lixeira?: boolean; empresa_id?: string } = {}): Promise<CargoResposta<Cargo[]>> {
    try {
      const query = new URLSearchParams();
      if (params.lixeira) query.set('lixeira', 'true');
      if (params.empresa_id) query.set('empresa_id', params.empresa_id);
      const qs = query.toString();
      return await apiClient.get<CargoResposta<Cargo[]>>(`/cargos${qs ? `?${qs}` : ''}`);
    } catch (error) {
      return erroCargo<Cargo[]>(error, 'Erro ao buscar cargos', []);
    }
  },

  async create(data: CargoCampos & { empresa_id?: string | null }): Promise<CargoResposta<Cargo | null>> {
    try {
      return await apiClient.post<CargoResposta<Cargo>>('/cargos', {
        nome: data.nome,
        quantidade_quadro: data.quantidade_quadro ?? null,
        departamento_id: data.departamento_id ?? null,
        ...(data.empresa_id ? { empresa_id: data.empresa_id } : {}),
      });
    } catch (error) {
      return erroCargo<Cargo | null>(error, 'Erro ao criar cargo', null);
    }
  },

  async update(id: string, data: CargoCampos): Promise<CargoResposta<Cargo | null>> {
    try {
      return await apiClient.put<CargoResposta<Cargo>>(`/cargos/${id}`, {
        nome: data.nome,
        quantidade_quadro: data.quantidade_quadro ?? null,
        departamento_id: data.departamento_id ?? null,
      });
    } catch (error) {
      return erroCargo<Cargo | null>(error, 'Erro ao atualizar cargo', null);
    }
  },

  /** Importa cargos de uma planilha (.xlsx/.csv: nome, quantidade, departamento). Superadmin passa a empresa. */
  async importar(file: File, empresaId?: string | null): Promise<ApiResponse<ResultadoImportacaoCargos | null>> {
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (empresaId) fd.append('empresa_id', empresaId);
      return await apiClient.postMultipart<ApiResponse<ResultadoImportacaoCargos>>('/cargos/importar', fd);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao importar cargos', data: null };
    }
  },

  /** Soft delete. Bloqueado (409 CARGO_EM_USO) se alguém ainda usa o cargo. */
  async remove(id: string): Promise<CargoResposta<boolean>> {
    try {
      return await apiClient.delete<CargoResposta<boolean>>(`/cargos/${id}`);
    } catch (error) {
      return erroCargo<boolean>(error, 'Erro ao excluir cargo', false);
    }
  },

  async restaurar(id: string): Promise<CargoResposta<Cargo | null>> {
    try {
      return await apiClient.post<CargoResposta<Cargo>>(`/cargos/${id}/restaurar`);
    } catch (error) {
      return erroCargo<Cargo | null>(error, 'Erro ao restaurar cargo', null);
    }
  },
};

// ============================================
// CADASTRO INTELIGENTE (pessoas + departamentos + cargos numa planilha)
// ============================================

const enviarCadastroInteligente = async (
  etapa: 'previa' | 'importar',
  file: File,
  empresaId?: string | null,
  enviarConvites = false,
): Promise<ApiResponse<ResultadoCadastroInteligente | null>> => {
  try {
    const fd = new FormData();
    fd.append('file', file);
    if (empresaId) fd.append('empresa_id', empresaId);
    if (enviarConvites) fd.append('enviar_convites', 'true');
    return await apiClient.postMultipart<ApiResponse<ResultadoCadastroInteligente>>(`/cadastro-inteligente/${etapa}`, fd);
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Erro ao processar a planilha', data: null };
  }
};

export const cadastroInteligenteService = {
  /** Valida a planilha e mostra o que seria criado, sem gravar nada. Superadmin passa a empresa. */
  previa: (file: File, empresaId?: string | null) => enviarCadastroInteligente('previa', file, empresaId),
  /** Grava pessoas, departamentos, cargos e gestores; com `enviarConvites`, os convites do dia saem logo. */
  importar: (file: File, empresaId?: string | null, enviarConvites = false) =>
    enviarCadastroInteligente('importar', file, empresaId, enviarConvites),

  /** Situação dos convites da empresa (pendentes, que falharam, enviados, limite do dia). */
  async convites(empresaId?: string | null): Promise<ApiResponse<ResumoConvites | null>> {
    try {
      const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
      return await apiClient.get<ApiResponse<ResumoConvites>>(`/cadastro-inteligente/convites${query}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao ler os convites', data: null };
    }
  },

  /** Envia os convites pendentes (e os que falharam), até o limite do dia. */
  async enviarConvites(empresaId?: string | null): Promise<ApiResponse<(ResumoConvites & { a_enviar: number }) | null>> {
    try {
      return await apiClient.post<ApiResponse<ResumoConvites & { a_enviar: number }>>(
        '/cadastro-inteligente/convites/enviar',
        empresaId ? { empresa_id: empresaId } : {},
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar os convites', data: null };
    }
  },
};

// ============================================
// ORGANOGRAMA SERVICE
// ============================================

export const organogramasService = {
  async getCanvas(id: string): Promise<ApiResponse<OrganogramaCanvasResponse | null>> {
    try {
      const cacheBuster = Date.now();
      return await apiClient.get<ApiResponse<OrganogramaCanvasResponse>>(`/organogramas/${id}/canvas?_=${cacheBuster}`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar canvas do organograma',
        data: null,
      };
    }
  },

  async saveCanvas(
    id: string,
    data: { nodes: OrganogramaCanvasNodePayload[]; edges: OrganogramaCanvasEdgePayload[]; updatedAt?: string },
  ): Promise<ApiResponse<{ updatedAt: string; idMapping?: Record<string, string>; edgeIdMapping?: Record<string, string> } | null>> {
    try {
      return await apiClient.put<ApiResponse<{ updatedAt: string; idMapping?: Record<string, string>; edgeIdMapping?: Record<string, string> }>>(`/organogramas/${id}/canvas`, data);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao salvar canvas do organograma',
        data: null,
      };
    }
  },

  async getAll(empresaId?: string): Promise<ApiResponse<Organograma[]>> {
    try {
      const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
      return await apiClient.get<ApiResponse<Organograma[]>>(`/organogramas${query}`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar organogramas',
        data: [],
      };
    }
  },

  /** Dados dos cadastros (departamentos, cargos, pessoas) para o gerador automático de organograma. */
  async getDadosGeracao(empresaId?: string): Promise<ApiResponse<DadosGeracao | null>> {
    try {
      const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
      return await apiClient.get<ApiResponse<DadosGeracao>>(`/organogramas/dados-geracao${query}`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar dados para gerar o organograma',
        data: null,
      };
    }
  },

  async getById(id: string): Promise<ApiResponse<Organograma | null>> {
    try {
      return await apiClient.get<ApiResponse<Organograma>>(`/organogramas/${id}`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar organograma',
        data: null,
      };
    }
  },

  async create(data: {
    empresa_id?: string;
    nome: string;
    descricao?: string | null;
    status?: Organograma['status'];
    tipo?: Organograma['tipo'];
    is_principal?: boolean;
    visibilidade?: Organograma['visibilidade'];
    theme_override?: Organograma['theme_override'];
  }): Promise<ApiResponse<Organograma>> {
    try {
      return await apiClient.post<ApiResponse<Organograma>>('/organogramas', data);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar organograma',
        data: {} as Organograma,
      };
    }
  },

  async update(id: string, data: Partial<Organograma>): Promise<ApiResponse<Organograma | null>> {
    try {
      return await apiClient.put<ApiResponse<Organograma>>(`/organogramas/${id}`, data);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar organograma',
        data: null,
      };
    }
  },

  async duplicate(id: string, nome?: string): Promise<ApiResponse<Organograma>> {
    try {
      return await apiClient.post<ApiResponse<Organograma>>(`/organogramas/${id}/duplicate`, { nome });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao duplicar organograma',
        data: {} as Organograma,
      };
    }
  },

  async archive(id: string): Promise<ApiResponse<Organograma | null>> {
    try {
      return await apiClient.patch<ApiResponse<Organograma>>(`/organogramas/${id}/archive`, {});
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao arquivar organograma',
        data: null,
      };
    }
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/organogramas/${id}`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir organograma',
        data: false,
      };
    }
  },

  async generateShareLink(id: string): Promise<ApiResponse<{ id: string; share_token: string; visibilidade: string } | null>> {
    try {
      return await apiClient.post<ApiResponse<{ id: string; share_token: string; visibilidade: string }>>(`/organogramas/${id}/share`, {});
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao gerar link público',
        data: null,
      };
    }
  },

  async revokeShareLink(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/organogramas/${id}/share`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao revogar link público',
        data: false,
      };
    }
  },

  async getPublicCanvasByToken(token: string): Promise<ApiResponse<(Organograma & { readonly: boolean; canEdit: boolean; nodes: OrganogramaCanvasNodePayload[]; edges: OrganogramaCanvasEdgePayload[] }) | null>> {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/public/organogramas/${token}/canvas`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Erro HTTP: ${response.status}`);
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao carregar canvas público',
        data: null,
      };
    }
  },

  async getHistorico(id: string): Promise<ApiResponse<OrganogramaHistorico[]>> {
    try {
      return await apiClient.get<ApiResponse<OrganogramaHistorico[]>>(`/organogramas/${id}/historico`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar histórico do organograma',
        data: [],
      };
    }
  },

  async getPermissoes(id: string): Promise<ApiResponse<OrganogramaPermissao[]>> {
    try {
      return await apiClient.get<ApiResponse<OrganogramaPermissao[]>>(`/organogramas/${id}/permissoes`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar permissões do organograma',
        data: [],
      };
    }
  },

  async updatePermissoes(id: string, permissoes: Array<{ usuario_id: string; role: 'owner' | 'editor' | 'viewer' }>): Promise<ApiResponse<OrganogramaPermissao[]>> {
    try {
      return await apiClient.put<ApiResponse<OrganogramaPermissao[]>>(`/organogramas/${id}/permissoes`, { permissoes });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar permissões do organograma',
        data: [],
      };
    }
  },
};

// ============================================
// TIPO DE ORGANOGRAMA SERVICE
// ============================================

export const tipoOrganogramaService = {
  async getAll(empresa_id?: string): Promise<ApiResponse<TipoOrganograma[]>> {
    try {
      const url = empresa_id ? `/tipos-organograma?empresa_id=${empresa_id}` : '/tipos-organograma';
      return await apiClient.get<ApiResponse<TipoOrganograma[]>>(url);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar tipos de organograma',
        data: [],
      };
    }
  },

  async create(data: { descricao: string; empresa_id?: string }): Promise<ApiResponse<TipoOrganograma>> {
    try {
      return await apiClient.post<ApiResponse<TipoOrganograma>>('/tipos-organograma', data);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar tipo de organograma',
        data: {} as TipoOrganograma,
      };
    }
  },
};

// Serviço legado do organograma em árvore. Mantido apenas o getAll,
// ainda consumido pelo FluxogramaEditor (responsável por nó do fluxograma).
export const organogramaService = {
  async getAll(empresaId?: string, organogramaId?: string): Promise<ApiResponse<OrganogramaNode[]>> {
    try {
      const params = new URLSearchParams();
      if (empresaId) params.set('empresa_id', empresaId);
      if (organogramaId) params.set('organograma_id', organogramaId);
      const query = params.toString() ? `?${params.toString()}` : '';
      return await apiClient.get<ApiResponse<OrganogramaNode[]>>(`/organograma${query}`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar organograma',
        data: [],
      };
    }
  },
};

// ============================================
// AUDIT LOG SERVICE
// ============================================

export interface AuditLogEntry {
  id: string;
  usuario_id: string;
  email: string;
  nome: string;
  ip: string | null;
  user_agent: string | null;
  createdAt: string;
}

export interface AuditLogResponse {
  success: boolean;
  data: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
}

export const auditLogService = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    from?: string;
    to?: string;
  }): Promise<AuditLogResponse> {
    try {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.search) query.set('search', params.search);
      if (params?.from) query.set('from', params.from);
      if (params?.to) query.set('to', params.to);

      const qs = query.toString();
      const response = await apiClient.get<AuditLogResponse>(`/audit-logs${qs ? `?${qs}` : ''}`);
      return response as AuditLogResponse;
    } catch (error) {
      return { success: false, data: [], total: 0, page: 1, limit: 50 };
    }
  },
};

// ============================================
// AUDITORIA EVENTO SERVICE (log genérico de alterações de dados — qualquer módulo)
// ============================================

export type AuditoriaAcao = 'criou' | 'atualizou' | 'excluiu' | 'ativou' | 'desativou';

export interface AuditoriaEventoEntry {
  id: string;
  empresa_id: string;
  usuario_id: string | null;
  usuario_nome: string;
  usuario_email: string;
  modulo: string;
  acao: AuditoriaAcao;
  entidade: string;
  entidade_id: string | null;
  entidade_titulo: string;
  alteracoes: Record<string, { de: unknown; para: unknown }> | null;
  descricao: string | null;
  createdAt: string;
}

export interface AuditoriaEventoResponse {
  success: boolean;
  data: AuditoriaEventoEntry[];
  /** `id → nome` dos registros referenciados no de/para (líder, tipo, departamento...). */
  nomes?: Record<string, string>;
  total: number;
  page: number;
  limit: number;
}

export const auditoriaEventoService = {
  /** Módulos distintos já gravados no escopo do usuário (alimenta o filtro da tela). */
  async getModulos(): Promise<string[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: string[] }>('/auditoria-eventos/modulos');
      return (response as { data: string[] }).data ?? [];
    } catch (error) {
      return [];
    }
  },

  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    from?: string;
    to?: string;
    modulo?: string;
    acao?: AuditoriaAcao;
  }): Promise<AuditoriaEventoResponse> {
    try {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.search) query.set('search', params.search);
      if (params?.from) query.set('from', params.from);
      if (params?.to) query.set('to', params.to);
      if (params?.modulo) query.set('modulo', params.modulo);
      if (params?.acao) query.set('acao', params.acao);

      const qs = query.toString();
      const response = await apiClient.get<AuditoriaEventoResponse>(`/auditoria-eventos${qs ? `?${qs}` : ''}`);
      return response as AuditoriaEventoResponse;
    } catch (error) {
      return { success: false, data: [], total: 0, page: 1, limit: 50 };
    }
  },
};

// ============================================
// NOTIFICACAO ENVIO SERVICE (monitoramento da outbox de notificações)
// ============================================

export type NotificacaoEnvioStatus = 'pendente' | 'processando' | 'enviado' | 'falha';

export interface NotificacaoEnvio {
  id: string;
  notificacao_id: string | null;
  empresa_id: string | null;
  usuario_id: string | null;
  modulo: string;
  evento: string;
  canal: string;
  destinatario: string;
  assunto: string | null;
  payload: Record<string, unknown>;
  status: NotificacaoEnvioStatus;
  tentativas: number;
  proxima_tentativa_em: string | null;
  enviado_em: string | null;
  erro: string | null;
  provider_message_id: string | null;
  createdAt: string;
  updatedAt: string;
  usuario?: { id: string; nome: string } | null;
  notificacao?: { id: string; titulo: string; mensagem?: string } | null;
}

export interface NotificacaoEnvioListResponse {
  success: boolean;
  data: NotificacaoEnvio[];
  total: number;
  page: number;
  limit: number;
}

export const notificacaoEnvioService = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    canal?: string;
    modulo?: string;
    evento?: string;
    destinatario?: string;
    from?: string;
    to?: string;
  }): Promise<NotificacaoEnvioListResponse> {
    try {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.status) query.set('status', params.status);
      if (params?.canal) query.set('canal', params.canal);
      if (params?.modulo) query.set('modulo', params.modulo);
      if (params?.evento) query.set('evento', params.evento);
      if (params?.destinatario) query.set('destinatario', params.destinatario);
      if (params?.from) query.set('from', params.from);
      if (params?.to) query.set('to', params.to);

      const qs = query.toString();
      const response = await apiClient.get<NotificacaoEnvioListResponse>(`/notificacao-envios${qs ? `?${qs}` : ''}`);
      return response as NotificacaoEnvioListResponse;
    } catch (error) {
      return { success: false, data: [], total: 0, page: 1, limit: 50 };
    }
  },

  async getById(id: string): Promise<ApiResponse<NotificacaoEnvio | null>> {
    try {
      return await apiClient.get<ApiResponse<NotificacaoEnvio | null>>(`/notificacao-envios/${id}`);
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Erro ao buscar envio',
      };
    }
  },

  async reenviar(id: string): Promise<ApiResponse<NotificacaoEnvio | null>> {
    try {
      return await apiClient.post<ApiResponse<NotificacaoEnvio | null>>(`/notificacao-envios/${id}/reenviar`, {});
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Erro ao reenviar notificação',
      };
    }
  },
};

// ============================================
// USUARIO PROFILE SERVICE (perfil 360 do usuário — super admin)
// ============================================

export interface UsuarioPerfilCompleto {
  usuario: Usuario & {
    empresa?: { id: string; nome: string } | null;
    departamento?: { id: string; nome: string } | null;
  };
  ultimo_acesso: string | null;
  total_acessos: number;
  pode_ver_auditoria: boolean;
}

/** Resposta de dias trabalhados — ver `backend/src/services/userProfile/diasTrabalhados.ts`.
 *  `dias`: YYYY-MM-DD → máscara (1 app, 2 agenda, 4 evento cadastrado, 8 ação registrada). */
export interface DiasTrabalhadosUsuario {
  dias: Record<string, number>;
  ultimo_registro: string | null;
  /** ISO (UTC). */
  ultimo_acesso: string | null;
  ultima_acao: { ts: string; modulo: string; descricao: string } | null;
  criado_em: string | null;
}

export interface DiasTrabalhadosResposta {
  hoje: string;
  usuarios: Record<string, DiasTrabalhadosUsuario>;
}

/** Aba Usuários > Módulos — ver `backend/src/services/userProfile/atividadeAgregada.ts`. */
export interface AtividadeAgregada {
  porSemana: { usuario_id: string; semana: string; modulo: string; total: number }[];
  /** Total do período inteiro por usuário e módulo (módulo em texto: 'Projetos', 'OKRs', ...). */
  porModulo: { usuario_id: string; modulo: string; total: number }[];
  porEmpresa: { usuario_id: string; empresa_id: string | null; empresa_nome: string | null; total: number }[];
}

export interface ModuloResumo {
  key: string;
  label: string;
  icon: string;
  indireto: boolean;
  total: number;
  extra: Record<string, number>;
  erro?: boolean;
}

export interface AtividadeResponse {
  success: boolean;
  data: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
}

export const usuarioProfileService = {
  async getPerfil(id: string): Promise<UsuarioPerfilCompleto | null> {
    try {
      const res = await apiClient.get<{ success: boolean; data: UsuarioPerfilCompleto }>(
        `/usuarios/${id}/perfil`
      );
      return res.success ? res.data : null;
    } catch {
      return null;
    }
  },

  async getResumo(id: string, empresaId?: string, periodo?: { de?: string; ate?: string }): Promise<ModuloResumo[]> {
    try {
      const query = new URLSearchParams();
      if (empresaId) query.set('empresa_id', empresaId);
      if (periodo?.de) query.set('de', periodo.de);
      if (periodo?.ate) query.set('ate', periodo.ate);
      const qs = query.toString() ? `?${query.toString()}` : '';
      const res = await apiClient.get<{ success: boolean; data: ModuloResumo[] }>(
        `/usuarios/${id}/resumo${qs}`
      );
      return res.success ? res.data : [];
    } catch {
      return [];
    }
  },

  async getDiasTrabalhados(id: string, periodo?: { de?: string; ate?: string }): Promise<DiasTrabalhadosResposta | null> {
    try {
      const query = new URLSearchParams();
      if (periodo?.de) query.set('de', periodo.de);
      if (periodo?.ate) query.set('ate', periodo.ate);
      const qs = query.toString() ? `?${query.toString()}` : '';
      const res = await apiClient.get<{ success: boolean; data: DiasTrabalhadosResposta }>(
        `/usuarios/${id}/dias-trabalhados${qs}`
      );
      return res.success ? res.data : null;
    } catch {
      return null;
    }
  },

  /** Lista inteira numa chamada só (Dashboard de Usuários). */
  async getDiasTrabalhadosLista(
    usuarioIds: string[],
    periodo?: { de?: string; ate?: string }
  ): Promise<DiasTrabalhadosResposta | null> {
    try {
      const res = await apiClient.post<{ success: boolean; data: DiasTrabalhadosResposta }>(
        '/usuarios/dias-trabalhados',
        { usuario_ids: usuarioIds, de: periodo?.de, ate: periodo?.ate }
      );
      return res.success ? res.data : null;
    } catch {
      return null;
    }
  },

  /** Itens da pessoa num módulo (OKRs, projetos, cronogramas, organogramas, fluxogramas) — formato
   *  varia por módulo, ver `backend/src/services/userProfile/itensDoUsuario.ts`. */
  async getItens<T = unknown>(id: string, modulo: string, empresaId?: string): Promise<T | null> {
    try {
      const qs = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
      const res = await apiClient.get<{ success: boolean; data: T }>(`/usuarios/${id}/itens/${modulo}${qs}`);
      return res.success ? res.data : null;
    } catch {
      return null;
    }
  },

  async getAtividadeAgregada(
    usuarioIds: string[],
    periodo?: { de?: string; ate?: string }
  ): Promise<AtividadeAgregada | null> {
    try {
      const res = await apiClient.post<{ success: boolean; data: AtividadeAgregada }>(
        '/usuarios/atividade-agregada',
        { usuario_ids: usuarioIds, de: periodo?.de, ate: periodo?.ate }
      );
      return res.success ? res.data : null;
    } catch {
      return null;
    }
  },

  async getAtividade(
    id: string,
    modulo: string,
    params?: { page?: number; limit?: number; empresaId?: string; de?: string; ate?: string }
  ): Promise<AtividadeResponse> {
    try {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.empresaId) query.set('empresa_id', params.empresaId);
      if (params?.de) query.set('de', params.de);
      if (params?.ate) query.set('ate', params.ate);
      const qs = query.toString();
      const res = await apiClient.get<AtividadeResponse>(
        `/usuarios/${id}/atividade/${modulo}${qs ? `?${qs}` : ''}`
      );
      return res as AtividadeResponse;
    } catch {
      return { success: false, data: [], total: 0, page: 1, limit: 20 };
    }
  },
};

// ============================================
// OKR SERVICE
// ============================================

// Tipos para mapeamento do backend
interface OKRBackend {
  id: string;
  titulo: string;
  descricao: string;
  empresa_id: string;
  departamento_id: string;
  responsavel_id?: string | null;
  status: OKRStatus;
  progresso_geral: number;
  data_inicio: string | Date;
  data_fim: string | Date;
  motivo_cancelamento?: string | null;
  data_cancelamento?: string | Date | null;
  cancelado_por?: string | null;
  keyResults?: KeyResultBackend[];
}

interface KeyResultBackend {
  id: string;
  okr_id: string;
  descricao: string;
  responsavel_id?: string | null;
  progresso_atual: number;
  progresso_meta: number;
  percentual: number;
  ordem: number;
  prazo?: string | Date | null;
  aprovacao_status?: 'none' | 'pending' | 'approved' | 'rejected' | null;
  aprovacao_progresso_solicitado?: number | null;
  aprovacao_solicitado_por?: string | null;
  aprovacao_solicitado_em?: string | Date | null;
  aprovacao_observacao?: string | null;
  aprovacao_motivo_rejeicao?: string | null;
  aprovacao_avaliado_por?: string | null;
  aprovacao_avaliado_em?: string | Date | null;
  anexo_obrigatorio?: boolean;
  anexo_url?: string | null;
  anexo_nome?: string | null;
  aprovar_alteracoes?: boolean;
}

// Funções de mapeamento
const mapOKRFromBackend = (okr: OKRBackend): OKR => {
  return {
    id: okr.id,
    titulo: okr.titulo,
    descricao: okr.descricao,
    empresa_id: okr.empresa_id,
    departamento_id: okr.departamento_id,
    responsavel_id: okr.responsavel_id ?? undefined,
    status: okr.status,
    progresso_geral: okr.progresso_geral,
    data_inicio: okr.data_inicio instanceof Date ? okr.data_inicio.toISOString().split('T')[0] : okr.data_inicio,
    data_fim: okr.data_fim instanceof Date ? okr.data_fim.toISOString().split('T')[0] : okr.data_fim,
    motivo_cancelamento: okr.motivo_cancelamento || undefined,
    data_cancelamento: okr.data_cancelamento 
      ? (okr.data_cancelamento instanceof Date ? okr.data_cancelamento.toISOString().split('T')[0] : okr.data_cancelamento)
      : undefined,
    cancelado_por: okr.cancelado_por || undefined,
  };
};

const mapKeyResultFromBackend = (kr: KeyResultBackend): KeyResult => {
  const aprovacaoStatus = kr.aprovacao_status || 'none';
  
  const aprovacao: KeyResultApproval | undefined = aprovacaoStatus !== 'none' ? {
    status: aprovacaoStatus,
    progresso_solicitado: kr.aprovacao_progresso_solicitado || undefined,
    solicitado_por: kr.aprovacao_solicitado_por || undefined,
    solicitado_em: kr.aprovacao_solicitado_em
      ? (kr.aprovacao_solicitado_em instanceof Date ? kr.aprovacao_solicitado_em.toISOString() : kr.aprovacao_solicitado_em)
      : undefined,
    observacao: kr.aprovacao_observacao || undefined,
    motivo_rejeicao: kr.aprovacao_motivo_rejeicao || undefined,
    avaliado_por: kr.aprovacao_avaliado_por || undefined,
    avaliado_em: kr.aprovacao_avaliado_em
      ? (kr.aprovacao_avaliado_em instanceof Date ? kr.aprovacao_avaliado_em.toISOString() : kr.aprovacao_avaliado_em)
      : undefined,
  } : undefined;

  // Garantir conversão explícita para boolean (MySQL pode retornar 0/1)
  const aprovarAlteracoes = kr.aprovar_alteracoes !== undefined && kr.aprovar_alteracoes !== null 
    ? Boolean(kr.aprovar_alteracoes) 
    : false;
  const anexoObrigatorio = kr.anexo_obrigatorio !== undefined && kr.anexo_obrigatorio !== null
    ? Boolean(kr.anexo_obrigatorio)
    : false;

  return {
    id: kr.id,
    okr_id: kr.okr_id,
    descricao: kr.descricao,
    responsavel_id: kr.responsavel_id ?? undefined,
    progresso_atual: kr.progresso_atual,
    progresso_meta: kr.progresso_meta,
    percentual: kr.percentual,
    ordem: kr.ordem,
    prazo: kr.prazo 
      ? (kr.prazo instanceof Date ? kr.prazo.toISOString().split('T')[0] : kr.prazo)
      : undefined,
    aprovacao,
    anexo_obrigatorio: anexoObrigatorio,
    aprovar_alteracoes: aprovarAlteracoes,
    anexo_url: kr.anexo_url || undefined,
    anexo_nome: kr.anexo_nome || undefined,
  };
};

export const okrService = {
  async getAll(): Promise<ApiResponse<OKR[]>> {
    try {
      const response = await apiClient.get<ApiResponse<OKRBackend[]>>('/okrs');
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapOKRFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar OKRs',
        data: [],
      };
    }
  },
  
  async getByEmpresa(empresaId: string): Promise<ApiResponse<OKR[]>> {
    try {
      const response = await apiClient.get<ApiResponse<OKRBackend[]>>(`/okrs?empresa_id=${empresaId}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapOKRFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar OKRs',
        data: [],
      };
    }
  },
  
  async getByDepartamento(departamentoId: string): Promise<ApiResponse<OKR[]>> {
    try {
      const response = await apiClient.get<ApiResponse<OKRBackend[]>>(`/okrs?departamento_id=${departamentoId}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapOKRFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar OKRs',
        data: [],
      };
    }
  },
  
  async getById(id: string): Promise<ApiResponse<OKR | null>> {
    try {
      const response = await apiClient.get<ApiResponse<OKRBackend>>(`/okrs/${id}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRFromBackend(response.data),
        };
      }
      return { ...response, data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar OKR',
        data: null,
      };
    }
  },
  
  async create(data: Omit<OKR, 'id' | 'progresso_geral'>): Promise<ApiResponse<OKR>> {
    try {
      const response = await apiClient.post<ApiResponse<OKRBackend>>('/okrs', data);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao criar OKR', data: data as OKR };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar OKR',
        data: data as OKR,
      };
    }
  },
  
  async update(id: string, data: Partial<OKR>): Promise<ApiResponse<OKR | null>> {
    try {
      const response = await apiClient.put<ApiResponse<OKRBackend>>(`/okrs/${id}`, data);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao atualizar OKR', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar OKR',
        data: null,
      };
    }
  },
  
  async updateStatus(id: string, status: OKR['status']): Promise<ApiResponse<OKR | null>> {
    try {
      const response = await apiClient.patch<ApiResponse<OKRBackend>>(`/okrs/${id}/status`, { status });
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao atualizar status', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar status',
        data: null,
      };
    }
  },
  
  async cancelar(id: string, motivo: string): Promise<ApiResponse<OKR | null>> {
    try {
      const response = await apiClient.post<ApiResponse<OKRBackend>>(`/okrs/${id}/cancelar`, { motivo });
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao cancelar OKR', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao cancelar OKR',
        data: null,
      };
    }
  },
  
  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(`/okrs/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir OKR',
        data: false,
      };
    }
  },
  
  async recalculateProgress(okrId: string): Promise<ApiResponse<OKR | null>> {
    try {
      const response = await apiClient.post<ApiResponse<OKRBackend>>(`/okrs/${okrId}/recalculate-progress`, {});
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao recalcular progresso', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao recalcular progresso',
        data: null,
      };
    }
  },
};

// ============================================
// KEY RESULT SERVICE
// ============================================

export const keyResultService = {
  async getAll(): Promise<ApiResponse<KeyResult[]>> {
    try {
      const response = await apiClient.get<ApiResponse<KeyResultBackend[]>>('/key-results');
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapKeyResultFromBackend).sort((a, b) => a.ordem - b.ordem),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar Key Results',
        data: [],
      };
    }
  },
  
  async getByOkr(okrId: string): Promise<ApiResponse<KeyResult[]>> {
    try {
      const response = await apiClient.get<ApiResponse<KeyResultBackend[]>>(`/key-results?okr_id=${okrId}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapKeyResultFromBackend).sort((a, b) => a.ordem - b.ordem),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar Key Results',
        data: [],
      };
    }
  },
  
  async getById(id: string): Promise<ApiResponse<KeyResult | null>> {
    try {
      const response = await apiClient.get<ApiResponse<KeyResultBackend>>(`/key-results/${id}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { ...response, data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar Key Result',
        data: null,
      };
    }
  },
  
  async create(data: Omit<KeyResult, 'id' | 'percentual'>): Promise<ApiResponse<KeyResult>> {
    try {
      const response = await apiClient.post<ApiResponse<KeyResultBackend>>('/key-results', data);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao criar Key Result', data: data as KeyResult };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar Key Result',
        data: data as KeyResult,
      };
    }
  },
  
  async update(id: string, data: Partial<KeyResult>): Promise<ApiResponse<KeyResult | null>> {
    try {
      const response = await apiClient.put<ApiResponse<KeyResultBackend>>(`/key-results/${id}`, data);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao atualizar Key Result', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar Key Result',
        data: null,
      };
    }
  },
  
  async updateProgress(id: string, progresso_atual: number, observacao?: string, anexoUrl?: string, anexoNome?: string): Promise<ApiResponse<KeyResult | null>> {
    try {
      const response = await apiClient.patch<ApiResponse<KeyResultBackend>>(`/key-results/${id}/progress`, {
        progresso_atual,
        observacao,
        anexo_url: anexoUrl,
        anexo_nome: anexoNome,
      });
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao atualizar progresso', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar progresso',
        data: null,
      };
    }
  },

  async uploadAnexo(id: string, file: File): Promise<ApiResponse<{ url: string; nome: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await apiClient.postMultipart<ApiResponse<{ url: string; nome: string }>>(
        `/key-results/${id}/upload-anexo`,
        formData
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar arquivo', data: { url: '', nome: '' } };
    }
  },

  async requestCompletion(id: string, progresso_solicitado: number, solicitadoPor: string, anexoUrl?: string, anexoNome?: string, observacao?: string): Promise<ApiResponse<KeyResult | null>> {
    try {
      const response = await apiClient.post<ApiResponse<KeyResultBackend>>(`/key-results/${id}/request-completion`, {
        progresso_solicitado,
        anexo_url: anexoUrl,
        anexo_nome: anexoNome,
        observacao,
      });
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao solicitar conclusão', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao solicitar conclusão',
        data: null,
      };
    }
  },

  async approveCompletion(id: string, avaliadoPor: string): Promise<ApiResponse<KeyResult | null>> {
    try {
      const response = await apiClient.post<ApiResponse<KeyResultBackend>>(`/key-results/${id}/approve`, {});
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao aprovar conclusão', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao aprovar conclusão',
        data: null,
      };
    }
  },

  async rejectCompletion(id: string, avaliadoPor: string, motivo: string): Promise<ApiResponse<KeyResult | null>> {
    try {
      const response = await apiClient.post<ApiResponse<KeyResultBackend>>(`/key-results/${id}/reject`, { motivo_rejeicao: motivo });
      if (response.success && response.data) {
        return {
          ...response,
          data: mapKeyResultFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao rejeitar conclusão', data: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao rejeitar conclusão',
        data: null,
      };
    }
  },

  async getPendingApprovals(empresaId?: string): Promise<ApiResponse<KeyResult[]>> {
    try {
      const url = empresaId ? `/key-results/pending-approvals?empresa_id=${empresaId}` : '/key-results/pending-approvals';
      const response = await apiClient.get<ApiResponse<KeyResultBackend[]>>(url);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapKeyResultFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar aprovações pendentes',
        data: [],
      };
    }
  },

  async countPendingApprovals(empresaId?: string): Promise<number> {
    try {
      const response = await this.getPendingApprovals(empresaId);
      return response.success ? response.data.length : 0;
    } catch (error) {
      return 0;
    }
  },

  async getApprovalHistory(empresaId?: string): Promise<ApiResponse<KeyResult[]>> {
    try {
      const url = empresaId ? `/key-results/approval-history?empresa_id=${empresaId}` : '/key-results/approval-history';
      const response = await apiClient.get<ApiResponse<KeyResultBackend[]>>(url);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapKeyResultFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar histórico de aprovações',
        data: [],
      };
    }
  },
  
  async reorder(okrId: string, krId: string, direction: 'up' | 'down'): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.post<ApiResponse<boolean>>(`/key-results/${krId}/reorder`, {
        okr_id: okrId,
        direction,
      });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao reordenar Key Result',
        data: false,
      };
    }
  },
  
  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(`/key-results/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir Key Result',
        data: false,
      };
    }
  },
};

// ============================================
// OKR ATIVIDADES SERVICE
// ============================================

interface OKRAtividadeBackend {
  id: string;
  okr_id: string;
  tipo: OKRAtividadeTipo;
  descricao: string;
  usuario_nome: string;
  data: string | Date;
  detalhes?: string | null;
}

interface PaginatedOKRAtividadesResponse extends ApiResponse<OKRAtividadeBackend[]> {
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OKRAtividadePaginationParams {
  page: number;
  limit?: number;
  empresa_id?: string;
  departamento_id?: string;
  okr_id?: string;
  sortField?: 'data' | 'okr' | 'tipo' | 'usuario';
  sortOrder?: 'asc' | 'desc';
}

export interface OKRAtividadePaginatedResult extends ApiResponse<OKRAtividade[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const mapOKRAtividadeFromBackend = (atividade: OKRAtividadeBackend): OKRAtividade => {
  return {
    id: atividade.id,
    okr_id: atividade.okr_id,
    tipo: atividade.tipo,
    descricao: atividade.descricao,
    usuario_nome: atividade.usuario_nome,
    data: atividade.data instanceof Date ? atividade.data.toISOString() : atividade.data,
    detalhes: atividade.detalhes || undefined,
  };
};

export const okrAtividadeService = {
  async getPaginated(params: OKRAtividadePaginationParams): Promise<OKRAtividadePaginatedResult> {
    try {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit ?? 10));
      if (params.empresa_id) query.set('empresa_id', params.empresa_id);
      if (params.departamento_id) query.set('departamento_id', params.departamento_id);
      if (params.okr_id) query.set('okr_id', params.okr_id);
      if (params.sortField) query.set('sortField', params.sortField);
      if (params.sortOrder) query.set('sortOrder', params.sortOrder);

      const response = await apiClient.get<PaginatedOKRAtividadesResponse>(`/okr-atividades?${query.toString()}`);
      const data = response.success && response.data ? response.data.map(mapOKRAtividadeFromBackend) : [];
      return {
        ...response,
        data,
        pagination: response.pagination ?? {
          page: params.page,
          limit: params.limit ?? 10,
          total: data.length,
          totalPages: 1,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar atividades',
        data: [],
        pagination: {
          page: params.page,
          limit: params.limit ?? 10,
          total: 0,
          totalPages: 0,
        },
      };
    }
  },

  async getByOkr(okrId: string): Promise<ApiResponse<OKRAtividade[]>> {
    try {
      const response = await apiClient.get<ApiResponse<OKRAtividadeBackend[]>>(`/okr-atividades/okr/${okrId}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapOKRAtividadeFromBackend),
        };
      }
      return { ...response, data: [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar atividades',
        data: [],
      };
    }
  },

  async create(data: Omit<OKRAtividade, 'id'>): Promise<ApiResponse<OKRAtividade>> {
    try {
      const response = await apiClient.post<ApiResponse<OKRAtividadeBackend>>('/okr-atividades', data);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapOKRAtividadeFromBackend(response.data),
        };
      }
      return { success: false, error: response.error || 'Erro ao criar atividade', data: data as OKRAtividade };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar atividade',
        data: data as OKRAtividade,
      };
    }
  },
};

// ============================================
// STATISTICS SERVICE (for dashboards)
// ============================================

export const statsService = {
  async getSuperAdminDashboard(): Promise<ApiResponse<{
    totalEmpresas: number;
    empresasAtivas: number;
    totalUsuarios: number;
    totalOkrs: number;
    okrsPorStatus: Record<string, number>;
    departments: Array<{ id: string; nome: string; okrCount: number; avgProgress: number; completionRate: number }>;
    usersByType: Array<{ tipo: string; count: number }>;
    okrsByCompany: Array<{ empresa: string; count: number }>;
    usersByCompany: Array<{ empresa: string; count: number }>;
    krProgress: Array<{ name: string; value: number }>;
  }>> {
    try {
      const response = await apiClient.get<ApiResponse<{
        totalEmpresas: number;
        empresasAtivas: number;
        totalUsuarios: number;
        totalOkrs: number;
        okrsPorStatus: Record<string, number>;
        departments: Array<{ id: string; nome: string; okrCount: number; avgProgress: number; completionRate: number }>;
        usersByType: Array<{ tipo: string; count: number }>;
        okrsByCompany: Array<{ empresa: string; count: number }>;
        usersByCompany: Array<{ empresa: string; count: number }>;
        krProgress: Array<{ name: string; value: number }>;
      }>>('/stats/superadmin/dashboard');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar dashboard superadmin',
        data: {
          totalEmpresas: 0,
          empresasAtivas: 0,
          totalUsuarios: 0,
          totalOkrs: 0,
          okrsPorStatus: {},
          departments: [],
          usersByType: [],
          okrsByCompany: [],
          usersByCompany: [],
          krProgress: [],
        },
      };
    }
  },

  async getSuperAdminStats(): Promise<ApiResponse<{
    totalEmpresas: number;
    empresasAtivas: number;
    totalUsuarios: number;
    totalOkrs: number;
    okrsPorStatus: Record<string, number>;
  }>> {
    try {
      const response = await apiClient.get<ApiResponse<{
        totalEmpresas: number;
        empresasAtivas: number;
        totalUsuarios: number;
        totalOkrs: number;
        okrsPorStatus: Record<string, number>;
      }>>('/stats/superadmin');
      return response;
    } catch (error) {
    return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar estatísticas',
      data: {
          totalEmpresas: 0,
          empresasAtivas: 0,
          totalUsuarios: 0,
          totalOkrs: 0,
          okrsPorStatus: {},
        },
      };
    }
  },
  
  async getAdminStats(empresaId: string): Promise<ApiResponse<{
    totalDepartamentos: number;
    totalUsuarios: number;
    totalOkrs: number;
    okrsPorStatus: Record<string, number>;
    progressoMedio: number;
  }>> {
    try {
      const response = await apiClient.get<ApiResponse<{
        totalDepartamentos: number;
        totalUsuarios: number;
        totalOkrs: number;
        okrsPorStatus: Record<string, number>;
        progressoMedio: number;
      }>>(`/stats/admin?empresa_id=${empresaId}`);
      return response;
    } catch (error) {
    return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar estatísticas',
      data: {
          totalDepartamentos: 0,
          totalUsuarios: 0,
          totalOkrs: 0,
          okrsPorStatus: {},
          progressoMedio: 0,
        },
      };
    }
  },
  
  async getUserStats(empresaId: string): Promise<ApiResponse<{
    totalOkrs: number;
    okrsPorStatus: Record<string, number>;
    progressoMedio: number;
  }>> {
    try {
      const response = await apiClient.get<ApiResponse<{
        totalOkrs: number;
        okrsPorStatus: Record<string, number>;
        progressoMedio: number;
      }>>(`/stats/user?empresa_id=${empresaId}`);
      return response;
    } catch (error) {
    return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar estatísticas',
      data: {
          totalOkrs: 0,
          okrsPorStatus: {},
          progressoMedio: 0,
        },
      };
    }
  },
};

// ============================================
// NOTIFICAÇÃO SERVICE - Backend Integration
// ============================================

const mapNotificacaoFromBackend = (notificacao: Notificacao): Notificacao => {
  return {
    ...notificacao,
    created_at: notificacao.created_at || notificacao.createdAt || '',
    updated_at: notificacao.updated_at || notificacao.updatedAt || '',
  };
};

export const notificacaoService = {
  async getAll(lida?: boolean, tipo?: string): Promise<ApiResponse<Notificacao[]>> {
    try {
      const params = new URLSearchParams();
      if (lida !== undefined) params.append('lida', lida.toString());
      if (tipo) params.append('tipo', tipo);
      
      const queryString = params.toString();
      const url = `/notificacoes${queryString ? `?${queryString}` : ''}`;
      const response = await apiClient.get<ApiResponse<Notificacao[]>>(url);
      if (response.success && response.data) {
        return {
          ...response,
          data: response.data.map(mapNotificacaoFromBackend),
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar notificações',
        data: [],
      };
    }
  },

  async getById(id: string): Promise<ApiResponse<Notificacao>> {
    try {
      const response = await apiClient.get<ApiResponse<Notificacao>>(`/notificacoes/${id}`);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapNotificacaoFromBackend(response.data),
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar notificação',
      };
    }
  },

  async marcarComoLida(id: string): Promise<ApiResponse<Notificacao>> {
    try {
      const response = await apiClient.patch<ApiResponse<Notificacao>>(`/notificacoes/${id}/lida`, {});
      if (response.success && response.data) {
        return {
          ...response,
          data: mapNotificacaoFromBackend(response.data),
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao marcar notificação como lida',
      };
    }
  },

  async marcarTodasComoLidas(): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.patch<ApiResponse<{ message: string }>>('/notificacoes/lidas/todas', {});
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao marcar todas as notificações como lidas',
      };
    }
  },

  async contarNaoLidas(): Promise<ApiResponse<{ count: number }>> {
    try {
      const response = await apiClient.get<ApiResponse<{ count: number }>>('/notificacoes/count');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao contar notificações não lidas',
        data: { count: 0 },
      };
    }
  },

  async create(data: {
    usuario_id: string;
    tipo: Notificacao['tipo'];
    titulo: string;
    mensagem: string;
    email_destinatario?: string;
    okr_id?: string;
    key_result_id?: string;
    relacionado_id?: string;
  }): Promise<ApiResponse<Notificacao>> {
    try {
      const response = await apiClient.post<ApiResponse<Notificacao>>('/notificacoes', data);
      if (response.success && response.data) {
        return {
          ...response,
          data: mapNotificacaoFromBackend(response.data),
        };
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar notificação',
      };
    }
  },

  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(`/notificacoes/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao deletar notificação',
      };
    }
  },
};

// ============================================
// USUARIO EMPRESA SERVICE
// ============================================

export const usuarioEmpresaService = {
  async getByUsuario(usuarioId: string): Promise<ApiResponse<UsuarioEmpresa[]>> {
    try {
      return await apiClient.get<ApiResponse<UsuarioEmpresa[]>>(`/usuario-empresas/${usuarioId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar empresas', data: [] };
    }
  },

  async create(data: { usuario_id: string; empresa_id: string; tipo: 'admin' | 'comum'; departamento_id?: string | null; cargo_id?: string | null; gestor_usuario_id?: string | null }): Promise<ApiResponse<UsuarioEmpresa>> {
    try {
      return await apiClient.post<ApiResponse<UsuarioEmpresa>>('/usuario-empresas', data);
    } catch (error) {
      return { success: false, error: mensagemDoErroCargoGestor(error, 'Erro ao adicionar acesso'), data: {} as UsuarioEmpresa };
    }
  },

  async update(id: string, data: { tipo?: 'admin' | 'comum'; departamento_id?: string | null; cargo_id?: string | null; gestor_usuario_id?: string | null }): Promise<ApiResponse<UsuarioEmpresa>> {
    try {
      return await apiClient.put<ApiResponse<UsuarioEmpresa>>(`/usuario-empresas/${id}`, data);
    } catch (error) {
      return { success: false, error: mensagemDoErroCargoGestor(error, 'Erro ao atualizar acesso'), data: {} as UsuarioEmpresa };
    }
  },

  async remove(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/usuario-empresas/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao remover acesso', data: false };
    }
  },
};

export const fichaCadastralService = {
  async get(usuarioId: string): Promise<ApiResponse<FichaCadastral | null>> {
    try {
      return await apiClient.get<ApiResponse<FichaCadastral | null>>(`/usuarios/${usuarioId}/ficha-cadastral`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar ficha cadastral', data: null };
    }
  },

  async update(usuarioId: string, data: Partial<FichaCadastral>): Promise<ApiResponse<FichaCadastral | null>> {
    try {
      return await apiClient.put<ApiResponse<FichaCadastral>>(`/usuarios/${usuarioId}/ficha-cadastral`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao salvar ficha cadastral', data: null };
    }
  },
};

export const catalogoService = {
  async listHabilidades(): Promise<ApiResponse<Habilidade[]>> {
    try {
      return await apiClient.get<ApiResponse<Habilidade[]>>('/catalogos/habilidades');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao listar habilidades', data: [] };
    }
  },

  async listCursosPessoais(): Promise<ApiResponse<CursoPessoal[]>> {
    try {
      return await apiClient.get<ApiResponse<CursoPessoal[]>>('/catalogos/cursos-pessoais');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao listar cursos', data: [] };
    }
  },
};

export const pushTokenService = {
  // O dono do token é sempre o usuário logado (o backend ignora qualquer user_id).
  async upsert(data: { token: string; platform: 'android' | 'ios' }): Promise<ApiResponse<null>> {
    try {
      return await apiClient.post<ApiResponse<null>>('/push-tokens', data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao registrar token de notificação', data: null };
    }
  },

  async remove(token: string): Promise<ApiResponse<null>> {
    try {
      return await apiClient.delete<ApiResponse<null>>('/push-tokens', { token });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao remover token de notificação', data: null };
    }
  },
};
